import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { useAuth } from "./AuthContext";
import { telephonyService, type ResolveOwnersResult } from "../services/telephonyService";
import { softphone, useSoftphone, type SoftphoneCall, type SoftphoneRueckfrage } from "../services/linkusSdk";
import {
    passendeUebergabe,
    UEBERGABE_FRIST_MS,
    uebergabeText,
    type OffeneUebergabe,
} from "../components/telephony/uebergabeNotiz";
import { hinweisNoetig } from "../services/softphoneAnnehmen";
import { headset, type HeadsetStatus } from "../services/headsetVerbindung";
import { audioGeraete } from "../services/audioGeraete";
import { soundSystem } from "../services/soundSystem";
import {
    anrufAnzeigeName,
    normalizeDialNumber,
} from "../components/telephony/telephonyUtils";
import {
    anrufHatVorgang,
    anrufZeileLaden,
    sollNachAnrufKarteZeigen,
    type AnrufKontext,
} from "../components/telephony/nachAnrufRegel";
import { vorgangIdsVon } from "../components/telephony/callZuordnungen";
import type { CallLogDto } from "../services/telephonyService";
import {
    entwurfLesen,
    entwurfLoeschen,
    entwurfSchreiben,
    meldeNotizGespeichert,
    notizAnAnrufSpeichern,
    notizKurz,
    offeneEntwuerfe,
} from "../components/telephony/anrufNotiz";
import { zeigeGlobalenHinweis } from "../components/calendar/globalerHinweis";
import {
    callLogIdAus,
    endeSignal,
    entscheideGeraet,
    KLINGEL_NOTIZ_FRIST_MS,
    notizenZusammen,
    passendeKlingelNotiz,
    TISCHTELEFON_CDR_WARTEN_MS,
    TISCHTELEFON_ENTSCHEIDUNG_MS,
    TISCHTELEFON_HOECHSTDAUER_MS,
    tischtelefonAnrufAus,
    wiederherzustellen,
    type KlingelNotizKandidat,
    type TischtelefonAnruf,
} from "../components/telephony/tischtelefon";
import { anrufEreignisseAbonnieren } from "../services/telefonieEreignisse";
import type { TelephonyEvent } from "../services/telephonyService";

/**
 * Wann ein nicht gespeicherter Notiz-Entwurf erneut versucht wird (ms nach dem
 * Auflegen). Findet der erste Versuch die Zeile nicht (Ingest gescheitert),
 * legt der CDR-Abgleich sie meist binnen ein, zwei Minuten an.
 */
const NOTIZ_WIEDERHOLUNGEN_MS = [0, 20_000, 90_000];

/**
 * Telephony — EINZIGE Quelle der Wahrheit für die In-App-Telefonie (SP2).
 *
 * Konsolidiert das frühere SSE-Inbound-Popup UND das SoftphoneWidget zu einem
 * einzigen, SDK-getriebenen Aktiv-Call-State (ADR 0001). Das Linkus Web SDK ist
 * Pflicht: Inbound-Klingeln läuft über das SDK-`incoming`-Event (nicht mehr SSE).
 * Die SSE-Schicht bleibt nur für Live-Refresh von Liste/Badges (in der
 * Telefon-Seite) — und ERGÄNZEND für eins (ADR 0001, Nachtrag): Wird am
 * Tischtelefon oder in der Linkus-App abgenommen, entzieht die Anlage dem
 * Browser den Anruf. Dann meldet `GET /telephony/events` die Annahme der
 * Nebenstelle, und das Fenster zeigt das Gespräch weiter („am Tischtelefon",
 * ohne Steuerung). Regeln in `tischtelefon.ts`.
 *
 * Der Context verbindet sich beim Mount automatisch mit dem SDK, WENN
 *   GET /telephony/sdk/status → enabled=true  UND
 *   GET /telephony/me        → eine Extension liefert.
 * Sonst bleibt `sdkStatus` idle/unsupported (kein Crash, keine UI).
 *
 * Gerendert über diesem Provider: `CallWidget` (schwebende Karte) + `ActiveCallBar`
 * (minimierte Statusleiste).
 */

export type SdkStatus = "idle" | "connecting" | "registered" | "unsupported" | "error";

export type ActiveCallState = "ringing" | "dialing" | "active";

export interface ActiveCall {
    callId: string;
    direction: "inbound" | "outbound";
    /** ringing = inbound klingelt; dialing = outbound wählt/klingelt; active = Gespräch */
    state: ActiveCallState;
    number: string | null;
    name: string | null;
    /**
     * ms-Timestamp, ab dem der Gesprächs-Timer läuft. Im Zustand 'active' ist das
     * der echte Annahme-Zeitpunkt (SDK `answeredAt`) — die Klingel-/Wählzeit zählt
     * NICHT mit. Vor der Annahme (ringing/dialing) der Anzeige-Fallback.
     */
    startedAt: number;
    isMute: boolean;
    isHold: boolean;
    /**
     * Das Gespräch läuft an einem anderen Gerät der Nebenstelle (Tischtelefon,
     * Linkus-App). callId ist dann die der Anlage; Stumm/Halten/Auflegen/DTMF/
     * Weiterleiten lassen sich vom Browser nicht steuern.
     */
    amTischtelefon: boolean;
    /** Kontakt laut Anlagen-Ereignis (nur am Tischtelefon; sonst über `owners`). */
    contactId: number | null;
}

/**
 * Ein gerade beendetes Gespräch, das nachbereitet werden will
 * (Nach-Anruf-Karte, P2). Gesetzt nur nach einem GEFÜHRTEN Gespräch und nur,
 * wenn Kontakt oder Vorgang noch fehlt (`sollNachAnrufKarteZeigen`).
 */
export interface NachAnrufInfo {
    callId: string;
    direction: "inbound" | "outbound";
    number: string | null;
    /** Name aus der Anlage bzw. beim Wählen mitgegeben. */
    pbxName: string | null;
    /** KMVS-Kontakt, wie er während des Gesprächs aufgelöst wurde. */
    kontaktName: string | null;
    dauerSec: number;
    /** Aus diesem Vorgang heraus wurde gewählt — die Karte schlägt ihn vor. */
    vorschlag: AnrufKontext | null;
    /** Die Zeile, falls beim Entscheiden schon geladen (sonst lädt die Karte). */
    call: CallLogDto | null;
}

interface TelephonyContextValue {
    sdkStatus: SdkStatus;
    activeCall: ActiveCall | null;
    /**
     * FO/BO-Verantwortlichkeit des aktuell aufgelösten Kontakts zum aktiven Anruf
     * (P5); `null`, solange (noch) nichts aufgelöst ist oder die Nummer keinem
     * sichtbaren Kontakt zugeordnet werden kann. Wird pro callId genau einmal
     * geladen und gecacht.
     */
    owners: ResolveOwnersResult | null;
    minimized: boolean;
    error: string | null;
    /**
     * Outbound wählen (app-weiter Helper; löst `tel:` ab). `kontext`: aus
     * welchem Vorgang/To-do heraus gewählt wird — die Nach-Anruf-Karte schlägt
     * ihn dann zur Zuordnung vor.
     */
    callNumber: (number: string, name?: string, kontext?: AnrufKontext) => void;
    answer: () => void;
    /** Beenden (aktiv/wählt) bzw. Ablehnen (inbound klingelt). */
    hangup: () => void;
    toggleMute: () => void;
    toggleHold: () => void;
    sendDtmf: (key: string) => void;
    blindTransfer: (n: string) => void;
    /**
     * Weiterleiten mit Rückfrage (08.10.2026): Anrufer halten und `n` anrufen.
     * Danach `rueckfrageVerbinden` oder `rueckfrageAbbrechen` (zurück zum Anrufer).
     */
    attendedTransfer: (n: string) => void;
    rueckfrage: SoftphoneRueckfrage | null;
    rueckfrageVerbinden: () => void;
    rueckfrageAbbrechen: () => void;
    minimize: () => void;
    restore: () => void;
    /** Schnellwahl-Overlay (aus dem Header-Telefon-Icon) — globaler Open-State. */
    schnellwahlOpen: boolean;
    openSchnellwahl: () => void;
    closeSchnellwahl: () => void;
    /** Das Gespräch, das die Nach-Anruf-Karte gerade nachbereitet (`null` = keins). */
    nachAnruf: NachAnrufInfo | null;
    /**
     * Die Karte schließen („Fertig"/„Später"). Ein noch nicht gespeicherter
     * Notiz-Entwurf wird dabei erneut versucht. `kontaktSpaeter` merkt die Nummer für diese Sitzung, damit
     * ein fehlender Kontakt allein die Karte nicht wieder aufmacht.
     */
    nachAnrufSchliessen: (opts?: { kontaktSpaeter?: boolean }) => void;
    /**
     * Notiz-Entwurf zu einer SDK-callId (auch schon beim Klingeln). Lebt hier
     * und in sessionStorage, bis er nach dem Auflegen am Anruf steht.
     */
    notizFuer: (callId: string) => string;
    setNotiz: (callId: string, text: string) => void;
    /**
     * Das Tischtelefon-Fenster von Hand schließen, falls das Ende-Signal der
     * Anlage ausbleibt. Behandelt wie ein Gesprächsende (Notiz, Nach-Anruf-Karte).
     */
    tischtelefonSchliessen: () => void;
    /**
     * Headset über WebHID (07.10.2026, Jabra): Abheben nimmt an, Einlegen legt
     * auf, die Stummtaste schaltet stumm. `headsetVerbinden` MUSS aus einem
     * Klick kommen (Browser-Freigabe), danach verbindet es sich selbst.
     */
    headset: HeadsetStatus;
    headsetVerbinden: () => void;
}

const TelephonyContext = createContext<TelephonyContextValue | undefined>(undefined);

/**
 * Wie `useTelephony`, wirft aber nicht ohne Provider — für Bausteine, die auch
 * in Tests und Vorschauen ohne Telefonie laufen (Anruf-Knopf der Kontaktanlage).
 */
export const useTelephonyOptional = (): TelephonyContextValue | undefined => useContext(TelephonyContext);

export const useTelephony = (): TelephonyContextValue => {
    const ctx = useContext(TelephonyContext);
    if (!ctx) throw new Error("useTelephony must be used within a TelephonyProvider");
    return ctx;
};

/** SDK-`callStatus` → unsere 3 fachlichen Zustände. */
function mapCallState(c: SoftphoneCall): ActiveCallState {
    switch (c.state) {
        case "talking":
            return "active";
        case "calling":
            return "dialing";
        case "ringing":
            // Inbound klingelt = ringing; outbound "remote ringing" = wählt.
            return c.direction === "outbound" ? "dialing" : "ringing";
        case "connecting":
        default:
            return c.direction === "outbound" ? "dialing" : "ringing";
    }
}

interface TelephonyProviderProps {
    children: ReactNode;
}

export const TelephonyProvider: React.FC<TelephonyProviderProps> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const sp = useSoftphone();
    const [minimized, setMinimized] = useState(false);
    const [schnellwahlOpen, setSchnellwahlOpen] = useState(false);

    // Name, der beim Wählen mitgegeben wurde (das SDK kennt ihn für Outbound oft
    // nicht) — als Fallback für die Anzeige, solange ein Anruf läuft.
    const dialNameRef = useRef<string | null>(null);
    // Anzeige-Fallback-Timestamp pro callId, solange das SDK noch kein answeredAt
    // liefert (ringing/dialing, vor der Annahme). Stabil pro callId, damit er bei
    // State-Wechseln nicht neu startet. Während des Renders gesetzt (reine
    // Identitäts-Verfolgung). Im Gespräch zählt stattdessen answeredAt (s. u.).
    const startedRef = useRef<{ id: string; at: number } | null>(null);

    // Gespräch am Tischtelefon / in der Linkus-App (s. `tischtelefon.ts`).
    // State treibt die Anzeige, der Ref ist der frische Stand für die
    // SSE-Handler (die hängen nicht am Render-Zyklus).
    const [tischAnruf, setTischAnrufState] = useState<TischtelefonAnruf | null>(null);
    const tischAnrufRef = useRef<TischtelefonAnruf | null>(null);
    const setTischAnruf = useCallback((a: TischtelefonAnruf | null) => {
        tischAnrufRef.current = a;
        setTischAnrufState(a);
    }, []);
    // Hat der Nutzer eine Nebenstelle? Nur dann lohnt der Ereignisstrom.
    const [hatNebenstelle, setHatNebenstelle] = useState(false);

    // ── Auto-Connect, wenn Feature aktiv UND eine Extension gemappt ist ──
    useEffect(() => {
        if (!isAuthenticated) {
            setHatNebenstelle(false);
            return;
        }
        let cancelled = false;
        let didConnect = false;
        void (async () => {
            const [status, me] = await Promise.all([
                telephonyService.sdkStatus().catch(() => ({ enabled: false })),
                telephonyService.myExtension().catch(() => null),
            ]);
            if (cancelled) return;
            setHatNebenstelle(!!me?.extension);
            if (status.enabled && me?.extension) {
                didConnect = true;
                void softphone.connect();
            }
        })();
        return () => {
            cancelled = true;
            if (didConnect) softphone.disconnect();
        };
    }, [isAuthenticated]);

    // ── activeCall ableiten (inkl. stabilem startedAt) ──
    const spCall = sp.activeCall;
    if (spCall) {
        if (startedRef.current?.id !== spCall.id) {
            startedRef.current = { id: spCall.id, at: Date.now() };
        }
    } else if (startedRef.current) {
        startedRef.current = null;
    }

    // Hat die Anlage „am Tischtelefon angenommen" gemeldet, während das
    // Softphone noch klingelt, gilt schon das Gespräch — das Klingeln bricht
    // die Anlage im nächsten Moment ab (07.10.2026: sofort als aktives
    // Telefonat zeigen, nicht erst ein verklingendes Klingelfenster).
    const spVerklingt =
        !!spCall && !!tischAnruf && spCall.direction === "inbound" && spCall.answeredAt == null &&
        mapCallState(spCall) === "ringing";
    const activeCall: ActiveCall | null = spCall && !spVerklingt
        ? {
              callId: spCall.id,
              direction: spCall.direction,
              state: mapCallState(spCall),
              number: spCall.number,
              name:
                  spCall.name ||
                  (spCall.direction === "outbound" ? dialNameRef.current : null),
              // Gesprächs-Timer ab Annahme (SDK answeredAt) — ohne Klingel-/Wählzeit.
              // Vor der Annahme der stabile Ring-/Wähl-Fallback nur für die Anzeige.
              startedAt: spCall.answeredAt ?? startedRef.current?.at ?? Date.now(),
              isMute: spCall.isMute,
              isHold: spCall.isHold,
              amTischtelefon: false,
              contactId: null,
          }
        : tischAnruf
          ? {
                // Kein Browser-Gespräch, aber eins am Tischtelefon: dasselbe
                // Fenster, nur ohne Steuerung. Ein im Browser klingelnder
                // weiterer Anruf (oben) geht vor — danach erscheint dies wieder.
                callId: tischAnruf.pbxCallId,
                direction: tischAnruf.direction,
                state: "active",
                number: tischAnruf.number,
                name: tischAnruf.name,
                startedAt: tischAnruf.answeredAt,
                isMute: false,
                isHold: false,
                amTischtelefon: true,
                contactId: tischAnruf.contactId,
            }
          : null;
    const aktiveCallId = activeCall?.callId ?? null;

    // Beim Anruf-Ende: Minimierung + gemerkten Namen zurücksetzen.
    useEffect(() => {
        if (!spCall) dialNameRef.current = null;
    }, [spCall]);
    useEffect(() => {
        if (!aktiveCallId) setMinimized(false);
    }, [aktiveCallId]);

    // ── FO/BO-Auflösung pro Anruf (P5) ──
    // Cache callId → aufgelöste Verantwortlichkeit (oder null = kein/kein
    // sichtbarer Kontakt). Pro callId genau EIN resolve-Aufruf; Re-Renders und
    // State-Wechsel (ringing→active) lösen keinen erneuten Call aus.
    const ownersCache = useRef<Map<string, ResolveOwnersResult | null>>(new Map());
    const [owners, setOwners] = useState<ResolveOwnersResult | null>(null);
    const ownerCallId = activeCall?.callId ?? null;
    const ownerNumber = activeCall?.number ?? null;
    useEffect(() => {
        if (!ownerCallId) {
            setOwners(null);
            return;
        }
        // Cache-Treffer (inkl. negativem null-Ergebnis) → ohne Netz übernehmen.
        if (ownersCache.current.has(ownerCallId)) {
            setOwners(ownersCache.current.get(ownerCallId) ?? null);
            return;
        }
        // Ohne Nummer ist nichts aufzulösen → negativ cachen.
        if (!ownerNumber) {
            ownersCache.current.set(ownerCallId, null);
            setOwners(null);
            return;
        }
        let cancelled = false;
        // Bis das Ergebnis da ist, keine veraltete Anzeige zeigen.
        setOwners(null);
        void telephonyService.resolveOwners(ownerNumber).then((res) => {
            ownersCache.current.set(ownerCallId, res);
            if (!cancelled) setOwners(res);
        });
        return () => {
            cancelled = true;
        };
    }, [ownerCallId, ownerNumber]);

    // ── Stand je laufendem Anruf (für den Moment nach dem Auflegen) ──
    //
    // Beim Auflegen ist `spCall` schon weg; Nummer, Name, Annahme und der
    // Vorgang, aus dem gewählt wurde, gibt es dann nur noch hier. Je callId,
    // weil beim Anklopfen zwei Gespräche gleichzeitig leben.
    const anrufStandRef = useRef<
        Map<
            string,
            {
                number: string | null;
                pbxName: string | null;
                direction: "inbound" | "outbound";
                answeredAt: number | null;
                kontext: AnrufKontext | null;
            }
        >
    >(new Map());
    // Vorgang, aus dem gerade gewählt wird — hängt sich an den nächsten
    // ausgehenden Anruf, der auftaucht (`callNumber` setzt ihn bei jedem Wählen neu).
    const dialKontextRef = useRef<AnrufKontext | null>(null);
    // Letzte Annahme im Browser — damit ein Anlagen-Signal, das zu einem eben
    // im Browser angenommenen (evtl. schon beendeten) Gespräch gehört, kein
    // Tischtelefon-Fenster aufmacht.
    const letzteSdkAnnahmeRef = useRef<number | null>(null);
    useEffect(() => {
        if (!spCall) return;
        if (spCall.answeredAt != null) {
            letzteSdkAnnahmeRef.current = Math.max(letzteSdkAnnahmeRef.current ?? 0, spCall.answeredAt);
        }
        const bisher = anrufStandRef.current.get(spCall.id);
        const ausgehend = spCall.direction === "outbound";
        anrufStandRef.current.set(spCall.id, {
            number: spCall.number,
            pbxName: spCall.name || bisher?.pbxName || (ausgehend ? dialNameRef.current : null),
            direction: spCall.direction,
            // answeredAt bleibt bis zum Ende gesetzt — einmal angenommen, immer angenommen.
            answeredAt: spCall.answeredAt ?? bisher?.answeredAt ?? null,
            kontext: bisher ? bisher.kontext : ausgehend ? dialKontextRef.current : null,
        });
        if (!bisher && ausgehend) dialKontextRef.current = null;
    }, [spCall]);

    // Nummern, zu denen der Benutzer in der Karte „Später" gesagt hat, ohne
    // einen Kontakt zuzuordnen — für diese Sitzung.
    const nichtMehrFragenRef = useRef<Set<string>>(new Set());
    const [nachAnruf, setNachAnruf] = useState<NachAnrufInfo | null>(null);
    const nachAnrufRef = useRef<NachAnrufInfo | null>(null);

    // ── Notiz aus dem Gespräch (P1) ──
    //
    // Entwurf je SDK-callId. Der State treibt die Anzeige, der Ref ist der
    // frische Stand für den Auflege-Moment (der Listener unten hängt nicht am
    // Render-Zyklus), sessionStorage die Sicherung gegen Neuladen.
    const [notizen, setNotizen] = useState<Record<string, string>>({});
    const notizenRef = useRef<Record<string, string>>({});
    const notizFuer = useCallback(
        (callId: string) => notizen[callId] ?? entwurfLesen(callId),
        [notizen],
    );
    const setNotiz = useCallback((callId: string, text: string) => {
        notizenRef.current = { ...notizenRef.current, [callId]: text };
        setNotizen(notizenRef.current);
        entwurfSchreiben(callId, text);
    }, []);

    // Pro callId läuft höchstens EIN Speicher-Vorgang (Auflegen und
    // Wiederaufnahme nach dem Laden dürfen sich nicht doppeln).
    const notizLaeuftRef = useRef<Set<string>>(new Set());
    // CallLog-IDs aus dem CDR-Ereignis für Tischtelefon-Gespräche (Anlagen-callId).
    const tischCallLogIdsRef = useRef<Map<string, Promise<number | null>>>(new Map());
    const notizSpeichern = useCallback((callId: string) => {
        if (notizLaeuftRef.current.has(callId)) return;
        notizLaeuftRef.current.add(callId);
        void (async () => {
            let text = "";
            for (const warte of NOTIZ_WIEDERHOLUNGEN_MS) {
                if (warte > 0) await new Promise((r) => window.setTimeout(r, warte));
                text = notizenRef.current[callId] ?? entwurfLesen(callId);
                const ergebnis = await notizAnAnrufSpeichern(callId, text, {
                    callLogId: softphone.callLogIdFor(callId) ?? tischCallLogIdsRef.current.get(callId) ?? null,
                    suchen: (id) =>
                        telephonyService
                            .listCalls({ callId: id, limit: 1 })
                            .then((r) => r.calls[0]?.id ?? null),
                    anhaengen: (id, inhalt) =>
                        telephonyService
                            .updateCall(id, { appendNotes: inhalt })
                            .then((call) => meldeNotizGespeichert({ callLogId: call.id, notes: call.notes ?? null })),
                });
                if (ergebnis === "gespeichert" || ergebnis === "leer") {
                    const { [callId]: _weg, ...rest } = notizenRef.current;
                    notizenRef.current = rest;
                    setNotizen(rest);
                    entwurfLoeschen(callId);
                    notizLaeuftRef.current.delete(callId);
                    if (ergebnis === "gespeichert") zeigeGlobalenHinweis("Notiz am Anruf gespeichert.");
                    return;
                }
            }
            // Alle Versuche gescheitert: Der Entwurf bleibt in diesem Tab
            // gesichert (beim nächsten Laden wird es erneut versucht) — und der
            // Text steht im Hinweis, damit er von Hand übertragen werden kann.
            notizLaeuftRef.current.delete(callId);
            zeigeGlobalenHinweis(
                `Die Notiz aus dem Gespräch konnte nicht am Anruf gespeichert werden. Sie bleibt in diesem Tab gesichert; bitte im Anruf-Verlauf eintragen: „${notizKurz(text, 160)}“`,
                { art: "fehler" },
            );
        })();
    }, []);

    const zeigeNachAnruf = useCallback(
        (info: NachAnrufInfo) => {
            // Eine noch offene Karte eines früheren Gesprächs macht Platz —
            // ihre Notiz wird dabei gespeichert, nicht verworfen.
            const alt = nachAnrufRef.current;
            if (alt && alt.callId !== info.callId) notizSpeichern(alt.callId);
            nachAnrufRef.current = info;
            setNachAnruf(info);
        },
        [notizSpeichern],
    );

    const nachAnrufSchliessen = useCallback(
        (opts?: { kontaktSpaeter?: boolean }) => {
            const info = nachAnrufRef.current;
            if (!info) return;
            const nummer = info.number?.trim();
            if (opts?.kontaktSpaeter && nummer) nichtMehrFragenRef.current.add(nummer);
            notizSpeichern(info.callId);
            nachAnrufRef.current = null;
            setNachAnruf(null);
        },
        [notizSpeichern],
    );

    // Ob nach einem geführten Gespräch die Nach-Anruf-Karte kommt: Zeile laden
    // und nur dann eine Karte, wenn Kontakt oder Vorgang fehlt (bzw. der
    // Vorgang, aus dem gewählt wurde, nicht zugeordnet ist). Für Browser- und
    // Tischtelefon-Gespräche gleich.
    const nachAnrufPruefen = useCallback(
        (info: NachAnrufInfo, callLogId: Promise<number | null> | null) => {
            void (async () => {
                const call = await anrufZeileLaden(info.callId, {
                    callLogId,
                    suchen: (id) =>
                        telephonyService
                            .listCalls({ callId: id, limit: 1, mitZuordnungen: true })
                            .then((r) => r.calls[0] ?? null),
                });
                // Kontakt: die Zeile zählt; fehlt er dort, bewusst frisch
                // auflösen — jemand kann ihn während des Telefonats angelegt haben.
                let hatKontakt = call?.contactId != null;
                if (!hatKontakt && info.number) {
                    hatKontakt = !!(await telephonyService.resolveOwners(info.number));
                }
                const zeigen = sollNachAnrufKarteZeigen({
                    gesprochen: true,
                    number: info.number,
                    hatKontakt,
                    hatVorgang: anrufHatVorgang(call),
                    vorgangIds: vorgangIdsVon(call),
                    vorschlag: info.vorschlag,
                    bereitsGefragt: nichtMehrFragenRef.current,
                });
                if (zeigen) zeigeNachAnruf({ ...info, call });
            })();
        },
        [zeigeNachAnruf],
    );

    // Im Browser verklungene, nicht angenommene eingehende Anrufe mit
    // Notiz-Entwurf — falls am Tischtelefon abgenommen wurde, zieht die beim
    // Klingeln getippte Notiz in dieses Gespräch um.
    const klingelNotizenRef = useRef<KlingelNotizKandidat[]>([]);
    const klingelNotizUebernehmen = useCallback(() => {
        const ziel = tischAnrufRef.current;
        if (!ziel) return;
        const jetzt = Date.now();
        klingelNotizenRef.current = klingelNotizenRef.current.filter(
            (k) => jetzt - k.endedAt <= KLINGEL_NOTIZ_FRIST_MS,
        );
        const von = passendeKlingelNotiz(klingelNotizenRef.current, ziel, jetzt);
        if (!von) return;
        klingelNotizenRef.current = klingelNotizenRef.current.filter((k) => k.callId !== von);
        const text = notizenRef.current[von] ?? entwurfLesen(von);
        if (!text.trim()) return;
        const bisher = notizenRef.current[ziel.pbxCallId] ?? entwurfLesen(ziel.pbxCallId);
        setNotiz(ziel.pbxCallId, notizenZusammen(bisher, text));
        // Der verzögerte Speicher-Versuch für den alten Entwurf findet dann nichts mehr.
        setNotiz(von, "");
    }, [setNotiz]);

    // ── Notiz vom Kollegen, der weiterleitet (08.10.2026) ──
    // Kommt vor oder während des Klingelns an; vorangestellt wird sie dem
    // Anruf, dessen Nummer passt (`uebergabeNotiz.ts`).
    const offeneUebergabenRef = useRef<OffeneUebergabe[]>([]);
    const uebergabeAnwenden = useCallback(() => {
        const c = softphone.getState().activeCall;
        if (!c || offeneUebergabenRef.current.length === 0) return;
        const u = passendeUebergabe(offeneUebergabenRef.current, c.number, Date.now());
        if (!u) return;
        offeneUebergabenRef.current = offeneUebergabenRef.current.filter((x) => x !== u);
        const bisher = notizenRef.current[c.id] ?? entwurfLesen(c.id);
        // notizenZusammen stellt den zweiten Text voran: die Übergabe oben.
        setNotiz(c.id, notizenZusammen(bisher, uebergabeText(u)));
    }, [setNotiz]);
    const uebergabeAnwendenRef = useRef(uebergabeAnwenden);
    uebergabeAnwendenRef.current = uebergabeAnwenden;
    useEffect(() => {
        if (spCall) uebergabeAnwenden();
    }, [spCall?.id, spCall?.number, uebergabeAnwenden]);

    // Gesprächsende — ausgelöst vom SDK, nicht vom Wechsel des aktiven Anrufs
    // (Anklopfen wechselt ihn, ohne dass etwas zu Ende wäre).
    //  - Eine im Gespräch (oder schon beim Klingeln) getippte Notiz wird sofort
    //    am Anruf gespeichert — in jedem Fall, die Karte zeigt sie nicht mehr (T-13).
    //    Ausnahme: Ein im Browser nicht angenommener eingehender Anruf wartet
    //    kurz — wurde am Tischtelefon abgenommen, gehört die Notiz dorthin.
    //  - Nicht gesprochen (verpasst/abgelehnt): keine Karte.
    //  - Sonst: `nachAnrufPruefen`.
    useEffect(
        () =>
            softphone.onCallEnded((callId) => {
                const stand = anrufStandRef.current.get(callId);
                anrufStandRef.current.delete(callId);
                const text = notizenRef.current[callId] ?? entwurfLesen(callId);
                if (text.trim()) {
                    if (stand && stand.answeredAt == null && stand.direction === "inbound") {
                        klingelNotizenRef.current.push({ callId, number: stand.number, endedAt: Date.now() });
                        klingelNotizUebernehmen();
                        window.setTimeout(() => notizSpeichern(callId), KLINGEL_NOTIZ_FRIST_MS);
                    } else {
                        notizSpeichern(callId);
                    }
                }
                if (!stand || stand.answeredAt == null) return;
                // Das Gespräch mit dem Kollegen in einer Rückfrage ist intern.
                if (softphone.warRueckfrage(callId)) return;
                nachAnrufPruefen(
                    {
                        callId,
                        direction: stand.direction,
                        number: stand.number,
                        pbxName: stand.pbxName,
                        kontaktName: ownersCache.current.get(callId)?.displayName ?? null,
                        dauerSec: Math.max(0, Math.round((Date.now() - stand.answeredAt) / 1000)),
                        vorschlag: stand.kontext,
                        call: null,
                    },
                    softphone.callLogIdFor(callId),
                );
            }),
        [notizSpeichern, nachAnrufPruefen, klingelNotizUebernehmen],
    );

    // ── Gespräch am Tischtelefon / in der Linkus-App (ADR 0001, Nachtrag) ──
    //
    // Quelle: `GET /telephony/events`. `incoming` heißt „die Nebenstelle hat
    // angenommen" — an welchem Gerät, entscheidet `entscheideGeraet` nach einer
    // kurzen Frist (das SDK-`accepted` kann nach dem Webhook kommen). Ende:
    // `ended` oder `cdr` derselben Anlagen-callId, sonst nach der Höchstdauer
    // bzw. von Hand. Danach wie beim Browser-Gespräch: Notiz an die CDR-Zeile,
    // Nach-Anruf-Karte.
    const entscheidungTimerRef = useRef<Map<string, number>>(new Map());
    const endeWartetRef = useRef<
        Map<string, { anruf: TischtelefonAnruf; endedAt: number; timer: number }>
    >(new Map());

    const tischAbschliessen = useCallback(
        (anruf: TischtelefonAnruf, endedAt: number, callLogId: number | null) => {
            const id = anruf.pbxCallId;
            const callLog = callLogId != null ? Promise.resolve(callLogId) : null;
            if (callLog) {
                tischCallLogIdsRef.current.set(id, callLog);
                while (tischCallLogIdsRef.current.size > 20) {
                    const aeltester = tischCallLogIdsRef.current.keys().next().value;
                    if (aeltester === undefined) break;
                    tischCallLogIdsRef.current.delete(aeltester);
                }
            }
            const text = notizenRef.current[id] ?? entwurfLesen(id);
            if (text.trim()) notizSpeichern(id);
            nachAnrufPruefen(
                {
                    callId: id,
                    direction: anruf.direction,
                    number: anruf.number,
                    pbxName: null,
                    kontaktName: ownersCache.current.get(id)?.displayName ?? anruf.name,
                    dauerSec: Math.max(0, Math.round((endedAt - anruf.answeredAt) / 1000)),
                    vorschlag: null,
                    call: null,
                },
                callLog,
            );
        },
        [notizSpeichern, nachAnrufPruefen],
    );

    // Fenster zu; mit CallLog-ID sofort abschließen, sonst kurz auf den CDR
    // warten (er bringt die ID und kommt ein paar Sekunden nach dem Auflegen).
    const tischBeenden = useCallback(
        (anruf: TischtelefonAnruf, endedAt: number, callLogId: number | null) => {
            if (tischAnrufRef.current?.pbxCallId === anruf.pbxCallId) setTischAnruf(null);
            if (endeWartetRef.current.has(anruf.pbxCallId)) return;
            if (callLogId != null) {
                tischAbschliessen(anruf, endedAt, callLogId);
                return;
            }
            const timer = window.setTimeout(() => {
                endeWartetRef.current.delete(anruf.pbxCallId);
                tischAbschliessen(anruf, endedAt, null);
            }, TISCHTELEFON_CDR_WARTEN_MS);
            endeWartetRef.current.set(anruf.pbxCallId, { anruf, endedAt, timer });
        },
        [setTischAnruf, tischAbschliessen],
    );

    const tischStarten = useCallback(
        (anruf: TischtelefonAnruf) => {
            const alt = tischAnrufRef.current;
            if (alt && alt.pbxCallId !== anruf.pbxCallId) tischBeenden(alt, Date.now(), null);
            setTischAnruf(anruf);
            setMinimized(false);
            klingelNotizUebernehmen();
        },
        [setTischAnruf, tischBeenden, klingelNotizUebernehmen],
    );

    const aufEreignis = (evt: TelephonyEvent) => {
        const jetzt = Date.now();
        if (evt.kind === "uebergabe") {
            const p = evt.payload ?? {};
            if (!p.notiz?.trim()) return;
            offeneUebergabenRef.current = [
                ...offeneUebergabenRef.current.filter((u) => jetzt - u.seit <= UEBERGABE_FRIST_MS),
                {
                    anruferNummer: p.fromNumber ?? null,
                    vonDurchwahl: p.extension ?? null,
                    vonName: p.vonName ?? null,
                    notiz: p.notiz,
                    seit: jetzt,
                },
            ];
            uebergabeAnwendenRef.current();
            return;
        }
        // Der CDR zu einem eben beendeten Gespräch bringt die CallLog-ID.
        const wartet = endeWartetRef.current.get(evt.callId);
        if (wartet) {
            if (evt.kind === "cdr") {
                window.clearTimeout(wartet.timer);
                endeWartetRef.current.delete(evt.callId);
                tischAbschliessen(wartet.anruf, wartet.endedAt, callLogIdAus(evt));
            }
            return;
        }
        const laufend = tischAnrufRef.current;
        if (laufend && endeSignal(evt, laufend.pbxCallId)) {
            tischBeenden(laufend, jetzt, callLogIdAus(evt));
            return;
        }
        if (evt.kind === "ended" || evt.kind === "cdr") {
            // Schon vorbei, bevor entschieden war — dann kein Fenster mehr.
            const t = entscheidungTimerRef.current.get(evt.callId);
            if (t != null) {
                window.clearTimeout(t);
                entscheidungTimerRef.current.delete(evt.callId);
            }
            return;
        }
        if (evt.kind !== "incoming") return;
        const anruf = tischtelefonAnrufAus(evt, jetzt);
        if (!anruf) return;
        // Ein wiederholtes ANSWERED (Retry, Fortsetzen nach Halten) setzt
        // weder Fenster noch Gesprächstimer zurück.
        if (laufend?.pbxCallId === anruf.pbxCallId || entscheidungTimerRef.current.has(anruf.pbxCallId)) return;
        const pruefen = () => {
            const c = softphone.getState().activeCall;
            const entscheidung = entscheideGeraet(
                {
                    anruf: c ? { direction: c.direction, state: c.state, answeredAt: c.answeredAt } : null,
                    letzteAnnahme: letzteSdkAnnahmeRef.current,
                    annehmenLaeuft: softphone.annehmenLaeuft(),
                },
                anruf.answeredAt,
                Date.now(),
            );
            if (entscheidung === "warten") {
                entscheidungTimerRef.current.set(
                    anruf.pbxCallId,
                    window.setTimeout(pruefen, TISCHTELEFON_ENTSCHEIDUNG_MS),
                );
                return;
            }
            entscheidungTimerRef.current.delete(anruf.pbxCallId);
            if (entscheidung === "tischtelefon") tischStarten(anruf);
        };
        // Sofort prüfen (07.10.2026) — gewartet wird nur, solange im Browser
        // ein „Annehmen" beim SDK liegt.
        pruefen();
    };
    const aufEreignisRef = useRef(aufEreignis);
    aufEreignisRef.current = aufEreignis;

    // Nach einem Neuaufbau des Stroms können `ended`/`cdr` gefehlt haben: Gibt
    // es zum laufenden Tischtelefon-Gespräch schon eine CDR-Zeile, ist es vorbei.
    const nachLueckePruefen = useCallback(() => {
        const laufend = tischAnrufRef.current;
        if (!laufend) return;
        void telephonyService
            .listCalls({ callId: laufend.pbxCallId, limit: 1 })
            .then((r) => {
                const zeile = r.calls[0];
                if (!zeile || tischAnrufRef.current?.pbxCallId !== laufend.pbxCallId) return;
                const dauer = zeile.talkDurationSec;
                const endedAt = dauer != null ? laufend.answeredAt + dauer * 1000 : Date.now();
                tischBeenden(laufend, endedAt, zeile.id);
            })
            .catch(() => {
                /* beim nächsten Signal bzw. über die Höchstdauer */
            });
    }, [tischBeenden]);
    const nachLueckePruefenRef = useRef(nachLueckePruefen);
    nachLueckePruefenRef.current = nachLueckePruefen;

    // Nach einem Neuladen (oder einer Lücke im Strom) das laufende Gespräch
    // wiederherstellen (07.10.2026: „bei einem Refresh muss das Popup weiter
    // angezeigt bleiben"). Die Ereignisse selbst wiederholt niemand — der
    // Server merkt sich die laufenden Gespräche (`GET /telephony/aktive-anrufe`).
    // Führt der Browser selbst ein Gespräch, gilt das.
    const ausServerWiederherstellen = useCallback(() => {
        void telephonyService
            .aktiveAnrufe()
            .then((liste) => {
                if (tischAnrufRef.current) return;
                const c = softphone.getState().activeCall;
                if (c && (c.answeredAt != null || c.direction === "outbound")) return;
                const anruf = wiederherzustellen(liste, Date.now());
                if (anruf) tischStarten(anruf);
            })
            .catch(() => {
                /* Beiwerk: ohne Antwort bleibt es beim nächsten Signal */
            });
    }, [tischStarten]);
    const ausServerWiederherstellenRef = useRef(ausServerWiederherstellen);
    ausServerWiederherstellenRef.current = ausServerWiederherstellen;

    useEffect(() => {
        if (!isAuthenticated || !hatNebenstelle) return;
        const entscheidungen = entscheidungTimerRef.current;
        const wartende = endeWartetRef.current;
        const abmelden = anrufEreignisseAbonnieren(
            {
                ereignis: (evt) => aufEreignisRef.current(evt),
                wiederVerbunden: () => {
                    nachLueckePruefenRef.current();
                    ausServerWiederherstellenRef.current();
                },
            },
            {
                oeffnen: () => (typeof EventSource === "undefined" ? null : telephonyService.openEventStream()),
                setTimeout: (fn, ms) => window.setTimeout(fn, ms),
                clearTimeout: (h) => window.clearTimeout(h as number),
            },
        );
        // Erstes Laden bzw. Neuladen: läuft schon ein Gespräch?
        ausServerWiederherstellenRef.current();
        return () => {
            abmelden();
            entscheidungen.forEach((t) => window.clearTimeout(t));
            entscheidungen.clear();
            wartende.forEach((w) => window.clearTimeout(w.timer));
            wartende.clear();
            // Beim Abmelden kein Fenster des alten Nutzers stehen lassen. Ein
            // Notiz-Entwurf bleibt in sessionStorage und wird beim nächsten
            // Laden nachgetragen.
            setTischAnruf(null);
        };
    }, [isAuthenticated, hatNebenstelle, setTischAnruf]);

    // Sicherheitsnetz: Gehen `ended` UND `cdr` verloren, schließt sich das
    // Fenster spätestens nach der Höchstdauer.
    useEffect(() => {
        if (!tischAnruf) return;
        const rest = tischAnruf.answeredAt + TISCHTELEFON_HOECHSTDAUER_MS - Date.now();
        const t = window.setTimeout(() => {
            const a = tischAnrufRef.current;
            if (a?.pbxCallId === tischAnruf.pbxCallId) tischBeenden(a, Date.now(), null);
        }, Math.max(0, rest));
        return () => window.clearTimeout(t);
    }, [tischAnruf, tischBeenden]);

    const tischtelefonSchliessen = useCallback(() => {
        const a = tischAnrufRef.current;
        if (a) tischBeenden(a, Date.now(), null);
    }, [tischBeenden]);

    // Nach einem Neuladen liegengebliebene Entwürfe (z. B. Reload mitten im
    // Gespräch — das beendet auch das SDK-Gespräch) einmal nachtragen.
    useEffect(() => {
        if (!isAuthenticated) return;
        const aktiv = softphone.getState().activeCall?.id ?? null;
        for (const { callId } of offeneEntwuerfe()) {
            if (callId !== aktiv) notizSpeichern(callId);
        }
    }, [isAuthenticated, notizSpeichern]);

    // ── Browser-Benachrichtigung bei EINGEHENDEM Anruf ──
    // Lokale Notification (kein Web-Push) für jeden neuen inbound klingelnden Anruf;
    // pro callId genau einmal, schließt sich bei Annahme/Ende automatisch. Erlaubnis
    // best-effort einholen, sobald die Extension registriert ist.
    const notifRef = useRef<Notification | null>(null);
    const notifiedCallIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (sp.status !== "registered") return;
        if (typeof Notification === "undefined") return;
        if (Notification.permission === "default") {
            void Notification.requestPermission().catch(() => {
                /* abgelehnt/blockiert → kein Crash, In-App-Popup bleibt */
            });
        }
    }, [sp.status]);
    const closeCallNotification = useCallback(() => {
        if (notifRef.current) {
            try {
                notifRef.current.close();
            } catch {
                /* ignore */
            }
            notifRef.current = null;
        }
    }, []);
    useEffect(() => {
        if (typeof Notification === "undefined") return;
        const inboundRinging =
            !!activeCall && activeCall.direction === "inbound" && activeCall.state === "ringing";
        if (!inboundRinging) {
            // Angenommen / beendet / kein eingehendes Klingeln → bestehende schließen.
            closeCallNotification();
            if (!activeCall) notifiedCallIdRef.current = null;
            return;
        }
        // Pro Anruf genau einmal. Permission ZUERST prüfen und die callId erst NACH dem
        // Versuch markieren — so zieht eine erst WÄHREND des Klingelns erteilte Erlaubnis
        // noch nach (sonst wäre der Anruf bereits „benachrichtigt" und bliebe stumm).
        if (notifiedCallIdRef.current === activeCall.callId) return;
        if (Notification.permission !== "granted") return;
        const who = anrufAnzeigeName({
            number: activeCall.number,
            pbxName: activeCall.name,
            kontaktName: owners?.displayName ?? null,
        });
        // Eine evtl. noch offene vorige Notification (z. B. Call-Waiting) schließen.
        closeCallNotification();
        try {
            const n = new Notification("Eingehender Anruf", {
                body: who,
                tag: `kmvs-call-${activeCall.callId}`,
                requireInteraction: true,
            });
            n.onclick = () => {
                try {
                    window.focus();
                } catch {
                    /* ignore */
                }
                n.close();
            };
            notifRef.current = n;
        } catch {
            /* Notification kann scheitern (z. B. iOS-Safari ohne PWA) → ignorieren */
        }
        notifiedCallIdRef.current = activeCall.callId;
    }, [activeCall, owners, closeCallNotification]);

    // Offene Anruf-Notification beim Unmount des Providers schließen (requireInteraction
    // ließe sie sonst stehen). Eigener Effekt mit []-Deps → läuft NUR beim Unmount.
    useEffect(() => () => closeCallNotification(), [closeCallNotification]);

    // ── Methoden (lesen den frischen SDK-State, um Stale-Closures zu vermeiden) ──
    const callNumber = useCallback((number: string, name?: string, kontext?: AnrufKontext) => {
        // Zentrale Normalisierung für ALLE Wähl-Einstiegspunkte (Dialpad, Roh-
        // Nummer, Suchtreffer, app-weite Click-to-call): führendes „+" behalten,
        // sonst nur Ziffern/`*`/`#`. Leere/ungültige Eingaben gar nicht erst werten.
        const n = normalizeDialNumber(number);
        if (!n) return;
        // User-Geste → guter Moment, die Notification-Erlaubnis best-effort zu erbitten
        // (für künftige eingehende Anrufe; Safari/iOS verlangt dafür eine Geste).
        if (typeof Notification !== "undefined" && Notification.permission === "default") {
            void Notification.requestPermission().catch(() => {});
        }
        // SDK nur ansprechen, wenn die Extension registriert ist — sonst wäre der
        // Dial ein stiller No-Op (linkusSdk.dial: `if (phone) …`). Statt wortlos
        // ins Leere zu laufen: dem User sichtbares Feedback geben, indem wir das
        // Schnellwahl-Overlay öffnen (zeigt den Verbindungs-Status: verbindet /
        // nicht verbunden / Fehler) + eine Warnung loggen. SDK ist Pflicht
        // (ADR 0001/0002), aber der Klick darf nicht stumm versanden.
        if (softphone.getState().status !== "registered") {
            console.warn(
                `[telephony] callNumber("${n}"): SDK nicht registriert (status=${softphone.getState().status}) — Anruf nicht möglich.`,
            );
            setMinimized(false);
            setSchnellwahlOpen(true);
            return;
        }
        dialNameRef.current = name?.trim() || null;
        dialKontextRef.current = kontext ?? null;
        setMinimized(false);
        setSchnellwahlOpen(false);
        void softphone.dial(n);
    }, []);

    const answer = useCallback(() => {
        // User-Geste → Notification-Erlaubnis nachholen, falls noch nicht entschieden.
        if (typeof Notification !== "undefined" && Notification.permission === "default") {
            void Notification.requestPermission().catch(() => {});
        }
        setMinimized(false);
        // T-233: Der Klick hat jetzt ein Ergebnis. Scheitert er, sagt das die
        // Oberfläche — vorher klingelte es einfach weiter, bis der Anrufer
        // auflegte, und niemand wusste, warum.
        const callId = softphone.getState().activeCall?.id ?? null;
        void softphone.answer().then((ergebnis) => {
            if (ergebnis.ok) return;
            // Erst kurz abwarten: kam das Gespräch trotzdem zustande oder hat
            // der Anrufer aufgelegt, wäre die Meldung falsch bzw. nutzlos
            // (07.10.2026: Fehlerfenster bei ic@, obwohl das Gespräch lief).
            window.setTimeout(() => {
                if (!hinweisNoetig(softphone.getState().activeCall, callId)) {
                    console.info("[telephony] Annehmen-Fehler ohne Hinweis — Anruf lief oder war vorbei", {
                        callId,
                        grund: ergebnis.grund,
                    });
                    return;
                }
                zeigeGlobalenHinweis(
                    `Der Anruf konnte nicht angenommen werden (${ergebnis.grund}). Bitte am Telefon oder in der Linkus-App annehmen und ein Ticket schreiben.`,
                    { art: "fehler" },
                );
            }, 1500);
        });
    }, []);

    const hangup = useCallback(() => {
        const c = softphone.getState().activeCall;
        // Inbound-Klingeln → ablehnen (reject); sonst auflegen.
        if (c && c.direction === "inbound" && c.state === "ringing") softphone.reject();
        else softphone.hangup();
    }, []);

    const toggleMute = useCallback(() => {
        const c = softphone.getState().activeCall;
        if (!c) return;
        if (c.isMute) softphone.unmute();
        else softphone.mute();
    }, []);

    // ── Headset (WebHID, 07.10.2026: Jabra statt Linkus-App) ──────────────
    // Tasten → Annehmen/Auflegen/Stumm; Anrufzustand → Lampen am Headset.
    const headsetAbonnieren = useCallback((fn: () => void) => headset.abonnieren(fn), []);
    const headsetStatus = useSyncExternalStore(headsetAbonnieren, () => headset.getStatus());
    useEffect(() => {
        headset.setHandler({
            lage: () => {
                const c = softphone.getState().activeCall;
                if (!c) return "frei";
                if (c.direction === "inbound" && c.answeredAt == null && c.state !== "talking") return "klingelt";
                return "aktiv";
            },
            aktion: (a) => {
                if (a === "abheben") answer();
                else if (a === "auflegen") hangup();
                else toggleMute();
            },
        });
        if (isAuthenticated && hatNebenstelle) {
            void headset.verbindeBekanntes();
            // Klingelton beim ersten Klick auf der Seite freischalten — sonst
            // hält Edge ihn bei einem Anruf ohne vorherige Bedienung stumm.
            soundSystem.freigabeBeiBedienung();
            // Klingelton über das Headset bzw. das gewählte Gerät (08.10.2026).
            audioGeraete.starten();
        }
        return () => headset.setHandler(null);
    }, [isAuthenticated, hatNebenstelle, answer, hangup, toggleMute]);
    useEffect(() => {
        const c = spCall;
        headset.setzeLampen({
            klingelt: !!c && c.direction === "inbound" && c.answeredAt == null && c.state !== "talking",
            abgehoben: !!c && (c.answeredAt != null || c.state === "talking" || c.direction === "outbound"),
            stumm: !!c?.isMute,
        });
    }, [spCall, headsetStatus.geraet]);
    // Neuladen/Schließen beendet ein Gespräch im Browser sofort (T-277, 09.10.:
    // Seite neu geladen, Gespräch weg). Dann fragt der Browser nach. Nur bei
    // einem laufenden oder gewählten Gespräch — Klingeln geht ja woanders weiter.
    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            const c = softphone.getState().activeCall;
            if (!c) return;
            const laeuft = c.answeredAt != null || c.state === "talking" || c.direction === "outbound";
            if (!laeuft) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, []);
    const headsetVerbinden = useCallback(() => {
        void headset.verbindeNeu();
    }, []);

    const toggleHold = useCallback(() => {
        const c = softphone.getState().activeCall;
        if (!c) return;
        if (c.isHold) softphone.unhold();
        else softphone.hold();
    }, []);

    const sendDtmf = useCallback((key: string) => softphone.dtmf(key), []);
    // Notiz beim Weiterleiten mitgeben (08.10.2026). Sie bleibt zusätzlich
    // am eigenen Anruf stehen; die Zustellung ist ein Zusatz, ihr Scheitern
    // stört das Weiterleiten nicht.
    const notizMitgeben = useCallback((ziel: string, anrufer: SoftphoneCall) => {
        const text = (notizenRef.current[anrufer.id] ?? entwurfLesen(anrufer.id)).trim();
        if (!text) return;
        void telephonyService
            .uebergabeNotiz({ ziel, anruferNummer: anrufer.number, text })
            .then((r) => {
                if (r.zugestellt) zeigeGlobalenHinweis("Notiz an den Kollegen weitergegeben.");
            })
            .catch((err) => console.warn("[telephony] Notiz-Übergabe fehlgeschlagen", err));
    }, []);
    const blindTransfer = useCallback(
        (n: string) => {
            const v = n.trim();
            const anrufer = softphone.getState().activeCall;
            if (!v || !anrufer) return;
            notizMitgeben(v, anrufer);
            softphone.blindTransfer(v);
        },
        [notizMitgeben],
    );
    const attendedTransfer = useCallback(
        (n: string) => {
            const v = n.trim();
            const anrufer = softphone.getState().activeCall;
            if (!v || !anrufer) return;
            // Vor dem Anruf beim Kollegen: Er sieht die Notiz schon in der Rückfrage.
            notizMitgeben(v, anrufer);
            void softphone.rueckfrageStarten(v).then((ok) => {
                if (!ok) zeigeGlobalenHinweis("Der Kollege konnte nicht angerufen werden. Der Anrufer ist wieder verbunden.", { art: "fehler" });
            });
        },
        [notizMitgeben],
    );
    const rueckfrageVerbinden = useCallback(() => softphone.rueckfrageVerbinden(), []);
    const rueckfrageAbbrechen = useCallback(() => softphone.rueckfrageAbbrechen(), []);

    const minimize = useCallback(() => setMinimized(true), []);
    const restore = useCallback(() => setMinimized(false), []);

    const openSchnellwahl = useCallback(() => setSchnellwahlOpen(true), []);
    const closeSchnellwahl = useCallback(() => setSchnellwahlOpen(false), []);

    const value: TelephonyContextValue = {
        sdkStatus: sp.status,
        activeCall,
        owners,
        minimized,
        error: sp.error,
        callNumber,
        answer,
        hangup,
        toggleMute,
        headset: headsetStatus,
        headsetVerbinden,
        toggleHold,
        sendDtmf,
        blindTransfer,
        attendedTransfer,
        rueckfrage: sp.rueckfrage,
        rueckfrageVerbinden,
        rueckfrageAbbrechen,
        minimize,
        restore,
        schnellwahlOpen,
        openSchnellwahl,
        closeSchnellwahl,
        nachAnruf,
        nachAnrufSchliessen,
        notizFuer,
        setNotiz,
        tischtelefonSchliessen,
    };

    return <TelephonyContext.Provider value={value}>{children}</TelephonyContext.Provider>;
};
